/**
 * TagDetailPage Component
 * View and edit tag details, manage assigned moves/series
 *
 * Features:
 * - Read-only and edit modes
 * - Display tag info with color indicator
 * - List assigned moves with remove option
 * - Add moves via drawer
 * - Series section (disabled/grayed out)
 * - Delete tag functionality
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Plus, Tag as TagIcon, X, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { ColorPicker, COLOR_OPTIONS } from '@/components/tags/ColorPicker'
import { ConfirmationModal } from '@/components/shared/ConfirmationModal'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ErrorState } from '@/components/shared/ErrorState'
import { AddItemsDrawer } from '@/components/shared/AddItemsDrawer'
import { useTag } from '@/hooks/useTagDetail'
import { useUpdateTag } from '@/hooks/useUpdateTag'
import { useDeleteTag } from '@/hooks/useDeleteTag'
import {
  useTaggings,
  useRemoveTagFromEntity,
  useAddTagsToEntity,
} from '@/hooks/useTaggings'
import { useMoves } from '@/hooks/useMoves'
import { useSeries } from '@/hooks/useSeriesList'
import type { UpdateTagDto } from '@/api/generated/api'
import type { TaggingWithTag } from '@/types/api-extensions'

export function TagDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Fetch tag data from API
  const { data: tagData, isLoading, isError, error, refetch } = useTag(id || '')

  // Fetch taggings (entities tagged with this tag)
  const {
    data: taggingsData,
    isLoading: isTaggingsLoading,
    refetch: refetchTaggings,
  } = useTaggings({
    tagId: id,
    includeRelations: true,
  })

  // Fetch moves and series to get names (since backend doesn't include relations)
  const { data: movesData } = useMoves({ limit: 100 })
  const { data: seriesData } = useSeries({ limit: 100 })

  // Create lookup maps for entity names
  const moveNamesMap = new Map(movesData?.moves?.map(m => [m.id, m.name]) || [])
  const seriesNamesMap = new Map(
    seriesData?.series?.map(s => [s.id, s.name]) || []
  )

  // Mutations
  const updateTagMutation = useUpdateTag()
  const deleteTagMutation = useDeleteTag()
  const removeTagMutation = useRemoveTagFromEntity()
  const addTagsToEntityMutation = useAddTagsToEntity()

  // UI state
  const [isEditMode, setIsEditMode] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isMoveDrawerOpen, setIsMoveDrawerOpen] = useState(false)
  const [isSeriesDrawerOpen, setIsSeriesDrawerOpen] = useState(false)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<{
    entityType: 'move' | 'series'
    entityId: string
    entityName: string
  } | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState<string | undefined>(undefined)
  const [nameError, setNameError] = useState('')

  // Sync form state with tag data when loaded
  useEffect(() => {
    if (tagData) {
      setName(tagData.name)
      setDescription(tagData.description || '')
      setColor(tagData.color || undefined)
    }
  }, [tagData])

  // Redirect if no ID
  if (!id) {
    navigate('/tags')
    return null
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9F9F9]">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <h1 className="mb-6 text-2xl font-semibold text-black">Loading...</h1>
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (isError || !tagData) {
    return (
      <div className="min-h-screen bg-[#F9F9F9]">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <h1 className="mb-6 text-2xl font-semibold text-black">Error</h1>
          <ErrorState
            error={error || new Error('Tag not found')}
            onRetry={refetch}
          />
        </div>
      </div>
    )
  }

  /**
   * Validate name field
   */
  const validateName = (value: string): boolean => {
    if (!value.trim()) {
      setNameError('Tag name is required')
      return false
    }
    if (value.length > 100) {
      setNameError('Tag name cannot exceed 100 characters')
      return false
    }
    setNameError('')
    return true
  }

  /**
   * Handle name input change with validation
   */
  const handleNameChange = (value: string) => {
    setName(value)
    if (nameError) {
      validateName(value)
    }
  }

  /**
   * Enter edit mode
   */
  const handleEdit = () => {
    setName(tagData.name)
    setDescription(tagData.description || '')
    setColor(tagData.color)
    setIsEditMode(true)
  }

  /**
   * Cancel edit mode
   */
  const handleCancelEdit = () => {
    setName(tagData.name)
    setDescription(tagData.description || '')
    setColor(tagData.color)
    setNameError('')
    setIsEditMode(false)
  }

  /**
   * Save tag updates
   */
  const handleSave = async () => {
    if (!validateName(name)) {
      return
    }

    const updateData: UpdateTagDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      color: color || undefined,
    }

    try {
      await updateTagMutation.mutateAsync({ tagId: id!, data: updateData })
      setIsEditMode(false)
      toast({
        title: 'Tag updated',
        description: 'Your changes have been saved successfully.',
      })
    } catch (error) {
      toast({
        title: 'Update failed',
        description: 'Failed to update tag. Please try again.',
        variant: 'destructive',
      })
    }
  }

  /**
   * Delete tag
   *
   * The hook owns the success/error UX (toast + navigate on success;
   * structured 409 parsing on failure). We `.catch(() => {})` the bare
   * promise to suppress the unhandled-rejection warning without re-showing
   * a redundant toast — the hook's onError already toasted.
   */
  const handleDelete = () => {
    deleteTagMutation.mutateAsync(id).catch(() => {})
  }

  /**
   * Remove tag from an entity (move or series)
   */
  const handleRemoveTag = (
    entityType: 'move' | 'series',
    entityId: string,
    entityName: string
  ) => {
    setRemoveTarget({ entityType, entityId, entityName })
    setShowRemoveConfirm(true)
  }

  /**
   * Confirm and execute tag removal
   */
  const handleConfirmRemove = async () => {
    if (!id || !removeTarget) return

    try {
      await removeTagMutation.mutateAsync({
        entityType: removeTarget.entityType,
        entityId: removeTarget.entityId,
        data: { tagId: id },
      })
      setShowRemoveConfirm(false)
      setRemoveTarget(null)
      toast({
        title: 'Tag removed',
        description: `Tag removed from "${removeTarget.entityName}".`,
      })
    } catch (error) {
      toast({
        title: 'Failed to remove tag',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  /**
   * Add this tag to selected moves
   */
  const handleAddTagToMoves = async (moveIds: string[]) => {
    if (!id) return

    try {
      // Add tag to each selected move
      for (const moveId of moveIds) {
        await addTagsToEntityMutation.mutateAsync({
          entityType: 'move',
          entityId: moveId,
          data: { tagIds: [id] },
        })
      }
      setIsMoveDrawerOpen(false)
      // Refetch taggings to show newly tagged moves
      await refetchTaggings()
      toast({
        title: 'Tag added to moves',
        description: `Tag added to ${moveIds.length} move${moveIds.length > 1 ? 's' : ''}.`,
      })
    } catch (error) {
      toast({
        title: 'Failed to add tag',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  /**
   * Add this tag to selected series
   */
  const handleAddTagToSeries = async (seriesIds: string[]) => {
    if (!id) return

    try {
      // Add tag to each selected series
      for (const seriesId of seriesIds) {
        await addTagsToEntityMutation.mutateAsync({
          entityType: 'series',
          entityId: seriesId,
          data: { tagIds: [id] },
        })
      }
      setIsSeriesDrawerOpen(false)
      // Refetch taggings to show newly tagged series
      await refetchTaggings()
      toast({
        title: 'Tag added to series',
        description: `Tag added to ${seriesIds.length} series.`,
      })
    } catch (error) {
      toast({
        title: 'Failed to add tag',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  /**
   * Get color name from hex value
   */
  const getColorName = (hexValue?: string) => {
    if (!hexValue) return 'No color'
    const colorOption = COLOR_OPTIONS.find(c => c.value === hexValue)
    return colorOption?.label || 'Custom'
  }

  // Filter taggings by type
  const allTaggings = (taggingsData?.taggings as TaggingWithTag[]) || []
  const taggedMoves = allTaggings.filter(t => t.taggableType === 'move')
  const taggedSeries = allTaggings.filter(t => t.taggableType === 'series')

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Main Content */}
      <main className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6">
        {/* Tag Info Section */}
        <section className="space-y-6 rounded-2xl border bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              Tag Information
            </h2>
            {isEditMode ? (
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={updateTagMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!name.trim()}
                  className="bg-[#0033A0] hover:bg-[#002080]"
                >
                  Save Changes
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={handleEdit}>
                  Edit Tag
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            )}
          </div>

          {isEditMode ? (
            <>
              {/* Edit Mode */}
              <div className="space-y-2">
                <label
                  htmlFor="tag-name"
                  className="block text-sm font-medium text-gray-900"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="tag-name"
                  type="text"
                  value={name}
                  onChange={e => handleNameChange(e.target.value)}
                  onBlur={() => validateName(name)}
                  maxLength={100}
                  className={cn(
                    'w-full rounded-lg border px-4 py-2',
                    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0033A0]',
                    nameError ? 'border-red-500' : 'border-gray-300'
                  )}
                  aria-invalid={!!nameError}
                  aria-describedby={nameError ? 'name-error' : undefined}
                />
                {nameError && (
                  <p id="name-error" className="text-sm text-red-500">
                    {nameError}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  {name.length}/100 characters
                </p>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="tag-description"
                  className="block text-sm font-medium text-gray-900"
                >
                  Description <span className="text-gray-400">(Optional)</span>
                </label>
                <textarea
                  id="tag-description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  className={cn(
                    'w-full resize-none rounded-lg border border-gray-300 px-4 py-2',
                    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0033A0]'
                  )}
                />
              </div>

              <ColorPicker
                value={color}
                onChange={setColor}
                label="Tag Color"
                showNoColor={true}
              />
            </>
          ) : (
            <>
              {/* Read-only Mode */}
              <div className="space-y-4">
                <div>
                  <h3 className="mb-1 text-sm font-medium text-gray-500">
                    Name
                  </h3>
                  <div className="flex items-center gap-3">
                    {color && (
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                    )}
                    <p className="text-base text-gray-900">{tagData.name}</p>
                  </div>
                </div>

                {tagData.description && (
                  <div>
                    <h3 className="mb-1 text-sm font-medium text-gray-500">
                      Description
                    </h3>
                    <p className="text-base text-gray-900">
                      {tagData.description}
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="mb-1 text-sm font-medium text-gray-500">
                    Color
                  </h3>
                  <div className="flex items-center gap-2">
                    {color ? (
                      <>
                        <div
                          className="h-6 w-6 rounded-full border"
                          style={{ backgroundColor: color }}
                          aria-hidden="true"
                        />
                        <span className="text-sm text-gray-700">
                          {getColorName(color)}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-500">No color</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </section>

        {/* Assigned Moves Section */}
        <section className="space-y-4 rounded-2xl border bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              Assigned Moves
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({taggedMoves.length})
              </span>
            </h2>
            <Button
              onClick={() => setIsMoveDrawerOpen(true)}
              className={cn(
                'flex items-center gap-2 rounded-lg px-4 py-2',
                'text-sm font-medium',
                'bg-[#0033A0] text-white hover:bg-[#002080]'
              )}
            >
              <Plus className="h-4 w-4" />
              Browse Moves
            </Button>
          </div>

          {isTaggingsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : taggedMoves.length === 0 ? (
            <div className="py-12 text-center">
              <TagIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <h3 className="mb-2 text-base font-medium text-gray-900">
                No moves tagged
              </h3>
              <p className="text-sm text-gray-500">
                Add this tag to moves to see them here.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {taggedMoves.map((tagging: TaggingWithTag) => (
                <div
                  key={tagging.id}
                  className="flex items-center justify-between rounded-lg border p-3 hover:bg-gray-50"
                >
                  <Link
                    to={`/moves/${tagging.taggableId}`}
                    className="flex-1 text-sm font-medium text-[#0033A0] hover:underline"
                  >
                    {moveNamesMap.get(tagging.taggableId) || 'Loading...'}
                  </Link>
                  <button
                    onClick={() =>
                      handleRemoveTag(
                        'move',
                        tagging.taggableId,
                        moveNamesMap.get(tagging.taggableId) || 'move'
                      )
                    }
                    className="text-gray-400 hover:text-red-600"
                    title="Remove tag"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Assigned Series Section */}
        <section className="space-y-4 rounded-2xl border bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              Assigned Series
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({taggedSeries.length})
              </span>
            </h2>
            <Button
              onClick={() => setIsSeriesDrawerOpen(true)}
              className={cn(
                'flex items-center gap-2 rounded-lg px-4 py-2',
                'text-sm font-medium',
                'bg-[#0033A0] text-white hover:bg-[#002080]'
              )}
            >
              <Plus className="h-4 w-4" />
              Browse Series
            </Button>
          </div>

          {isTaggingsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : taggedSeries.length === 0 ? (
            <div className="py-12 text-center">
              <TagIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <h3 className="mb-2 text-base font-medium text-gray-900">
                No series tagged
              </h3>
              <p className="text-sm text-gray-500">
                Add this tag to series to see them here.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {taggedSeries.map((tagging: TaggingWithTag) => (
                <div
                  key={tagging.id}
                  className="flex items-center justify-between rounded-lg border p-3 hover:bg-gray-50"
                >
                  <Link
                    to={`/series/${tagging.taggableId}`}
                    className="flex-1 text-sm font-medium text-[#0033A0] hover:underline"
                  >
                    {seriesNamesMap.get(tagging.taggableId) || 'Loading...'}
                  </Link>
                  <button
                    onClick={() =>
                      handleRemoveTag(
                        'series',
                        tagging.taggableId,
                        seriesNamesMap.get(tagging.taggableId) || 'series'
                      )
                    }
                    className="text-gray-400 hover:text-red-600"
                    title="Remove tag"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Add Moves Drawer */}
      <AddItemsDrawer
        isOpen={isMoveDrawerOpen}
        onClose={() => setIsMoveDrawerOpen(false)}
        title="Add Tag to Moves"
        items={(movesData?.moves || []).map(move => ({
          id: move.id,
          name: move.name,
          description: move.description || '',
          thumbnailUrl: move.thumbnailUrl,
        }))}
        currentItemIds={taggedMoves.map((t: TaggingWithTag) => t.taggableId)}
        onAddItems={handleAddTagToMoves}
        searchPlaceholder="Search moves..."
        addButtonLabel="Add Tag to Selected"
        emptyMessage="No moves available"
        emptySearchMessage="No moves match your search"
      />

      {/* Add Series Drawer */}
      <AddItemsDrawer
        isOpen={isSeriesDrawerOpen}
        onClose={() => setIsSeriesDrawerOpen(false)}
        title="Add Tag to Series"
        items={(seriesData?.series || []).map(series => ({
          id: series.id,
          name: series.name,
          description: series.description || '',
        }))}
        currentItemIds={taggedSeries.map((t: TaggingWithTag) => t.taggableId)}
        onAddItems={handleAddTagToSeries}
        searchPlaceholder="Search series..."
        addButtonLabel="Add Tag to Selected"
        emptyMessage="No series available"
        emptySearchMessage="No series match your search"
      />

      {/* Remove Tag from Entity Confirmation */}
      <ConfirmationModal
        isOpen={showRemoveConfirm}
        onClose={() => {
          setShowRemoveConfirm(false)
          setRemoveTarget(null)
        }}
        onConfirm={handleConfirmRemove}
        title="Remove Tag?"
        description={`Are you sure you want to remove this tag from "${removeTarget?.entityName || 'this entity'}"?`}
        confirmText="Remove"
        confirmVariant="destructive"
      />

      {/* Delete Tag Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Tag?"
        description={`Are you sure you want to delete "${tagData.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />
    </div>
  )
}
